import { html, css } from 'lit';
import { CrossBrandItem, Recommendations } from '../../server/types.js';
import { getItemBadge, trackClickEvent } from './utils/index.js';
import './recommendation-badge.js';
import './favorite-heart.js';
import { i18n } from '@lingui/core';
import WarpElement from '@warp-ds/elements-core';
import { customElement } from 'lit/decorators/custom-element.js';
import { formatCurrency, CurrencyCode } from '@borealis/number-format';

@customElement('cross-brand-card')
export class CrossBrandCard extends WarpElement {
	static styles = [
		...WarpElement.styles,
		css`
			.custom-label-style {
				background: linear-gradient(
					transparent,
					rgba(0, 0, 0, 0.04935) 13.71%,
					/* ... (rest of the gradient styles) */ rgba(0, 0, 0, 0.78731) 89.73%,
					rgba(0, 0, 0, 0.8)
				);
				width: 100%;
			}
			.two-lines {
				display: -webkit-box;
				-webkit-box-orient: vertical;
				-webkit-line-clamp: 2;
				overflow: hidden;
			}

			/** The line below is where the injected CSS goes, removing it means you get no CSS from the design system **/
			*,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0}.text-body{font-size:var(--w-font-size-m);line-height:var(--w-line-height-m)}.aspect-square{padding-bottom:100%;position:relative}.aspect-square>*{width:100%;height:100%;position:absolute;inset:0}.rounded-t-8{border-top-left-radius:8px;border-top-right-radius:8px}.hover\:no-underline:hover{text-decoration:none}.order-first{order:-9999}.static{position:static}.hover\:s-bg-hover:hover{background-color:var(--w-s-color-background-hover)}.active\:s-bg-active:active{background-color:var(--w-s-color-background-active)}.m-16{margin:1.6rem}.-mx-4{margin-left:-.4rem;margin-right:-.4rem}.px-4{padding-left:.4rem;padding-right:.4rem}.pb-10{padding-bottom:1rem}.pb-2{padding-bottom:.2rem}.pb-28{padding-bottom:2.8rem}.pl-10{padding-left:1rem}.pr-16{padding-right:1.6rem}.pt-6{padding-top:.6rem}.pt-\[88px\]{padding-top:88px}.object-cover{object-fit:cover}.object-center{object-position:center}.text-ellipsis{text-overflow:ellipsis};
		`,
	];

	item!: CrossBrandItem;
	data!: Recommendations;

	static properties = {
		item: { type: Object },
		data: { type: Object },
		locale: { type: String },
	};

	getCurrencyForLocale(): CurrencyCode {
		type SupportedLocale = 'fi' | 'sv' | 'nb' | 'da';
		type SupportedCurrency = 'EUR' | 'SEK' | 'NOK' | 'DKK';
		const localeCurrencyMap: Record<SupportedLocale, SupportedCurrency> = {
			fi: 'EUR',
			sv: 'SEK',
			nb: 'NOK',
			da: 'DKK',
		};
		if (!localeCurrencyMap[this.locale as SupportedLocale]) {
			console.warn(`No currency found for locale: ${this.locale}`);
			return 'EUR';
		}
		return localeCurrencyMap[this.locale as SupportedLocale];
	}

	render() {
		if (!this.item.subheading || !this.item.image) {
			return;
		} else {
			return html`
        <article aria-labelledby="card-title">
          <w-card>
            <div class="flex flex-col">
              <div class="m-16 flex flex-col">
                <!-- 1. Title -->
                <h3 id="card-title" class="text-body font-normal px-4 pt-6 pb-2 mb-0 -mx-4">
                  <a
                    class="s-text hover:no-underline text-md break-words text-ellipsis two-lines"
                    href="${this.item.url}"
                    target="_blank"
                    @click="${() => trackClickEvent(this.item, this.data)}"
                  >
                    <span class="absolute inset-0 z-10" aria-hidden="true"></span>
                    ${this.item.heading}
                  </a>
                </h3>
                <!-- 2. Badge -->
                <recommendation-badge class="z-10" .badge="${getItemBadge(this.item.type, this.item.badge)}" locale="${this.locale}"></recommendation-badge>
                <!-- 3. Price -->
                <span class="sr-only">
									${this.item.overlay
                    ? html`
											<span>
												<div
													class="absolute bottom-0 pb-28"
												>
													${this.item.overlay.price && this.locale !== 'en'
														? html`<span
														>${formatCurrency(this.item.overlay.price, this.locale, this.getCurrencyForLocale())}</span
														>`
														: ''}
													${this.item.overlay.text ? html`<span>${this.item.overlay.text}</span>` : ''}
												</div>
											</span>
										`
                    : ''}
								</span>
								<!-- 4. Location -->
								<div class="text-s s-text-subtle break-words text-ellipsis two-lines order-first">
									<span>${this.item.subheading}</span>
								</div>
              </div>
							<!-- 5. Image -->
              ${this.item.image ? this.renderImage() : ''}
            </div>
          </w-card>
        </article>
      `;
		}
	}

	private renderImage() {
		const imageSrc = this.item.image?.url;

		return html`
			<div class="aspect-square overflow-hidden border rounded-t-8 s-bg hover:s-bg-hover active:s-bg-active order-first">
				<img
					class="w-full h-full object-center object-cover m-auto"
					src="${imageSrc}"
					alt="${i18n.t({ id: 'frontpage.recommendations.images.alt.text', message: 'Photo from ad' })}"
				/>
				${this.item.overlay
					? html`
							<span aria-hidden="true">
								<div
									class="absolute bottom-0 font-bold s-text-inverted-static pt-[88px] pb-10 pl-10 pr-16 custom-label-style"
								>
									${this.item.overlay.price && this.locale !== 'en'
										? html`<span
												>${formatCurrency(this.item.overlay.price, this.locale, this.getCurrencyForLocale())}</span
											>`
										: ''}
									${this.item.overlay.text ? html`<span>${this.item.overlay.text}</span>` : ''}
								</div>
							</span>
						`
					: ''}
			</div>
		`;
	}
}
